// src/lib/drafts.ts

import { create } from "zustand";
import { toast } from "@/lib/toast";
import { Draft } from "@/types/draft";
import type { Tables, TablesUpdate } from "@/types/database";
import { supabase } from "@/lib/supabase";
import { leaveRealtimeChannel, uniqueChannelTopic } from "@/lib/realtime";
import { mapSupabaseTrackToDraft } from "./upload/utils/track-mapper";
import { fetchVersionsForParents, attachVersions } from "./draft-versions";
import { TrackLicensingService } from "./tracks";
import { UserCacheService } from "./user-cache";
import { LabelCacheService } from "./labels";
import { ApiError, apiClient } from "./api-client";
import {
  MAX_MAINS_PER_PUBLISH,
  TrackFamilyRefusal,
  attachVersion as attachVersionRequest,
  detachVersion as detachVersionRequest,
  familyRefusalOf,
  getFamily,
  publishFamilies,
  refusalMessage,
  type AttachVersionResult,
  type PublishFamilyResult,
  type TrackFamilyReason,
} from "./track-family";
import {
  validateDraftLicense,
  validateMultipleDraftLicenses,
} from "./license-validation";
import { ilikeContainsPattern, toOrFilterSearchTerm } from "@/lib/search-ranking";

/** The uploader's name travels with the page when the profiles relationship is available. */
const DRAFT_SELECT_WITH_UPLOADER =
  "*, profiles!tracks_user_id_profiles_fkey(display_name)";

const PAGE_SIZE = 30;

/**
 * How long this client treats the realtime echo of its OWN operation as expected news.
 *
 * The row still leaves the list when the echo arrives, but without the toast that tells a
 * seller a colleague moved their track: they pressed the button themselves and already saw
 * the result. Comfortably longer than a round trip, short enough that a colleague acting on
 * the same track a moment later is still announced.
 */
const OWN_OP_ECHO_MS = 10_000;

/**
 * A family whose rows moved is re-read once, this long after the last change.
 *
 * Publishing, attaching or deleting a family writes N rows, and every open client of the
 * label hears all N. Without the debounce each of them would refetch the same parent N times.
 */
const VERSIONS_REFETCH_DEBOUNCE_MS = 400;

/** A drafts page row: the track columns, plus the uploader embed when the page carried one. */
type DraftTrackRow = Tables<"tracks"> & {
  profiles?:
    | { display_name: string | null }
    | Array<{ display_name: string | null }>
    | null;
};

/**
 * The query behind the drafts on screen.
 *
 * A fetch commits it in the same `set` as its rows, so whatever describes those rows — the list's
 * grouping, the realtime matcher, the next page — reads this and not `searchQuery`: the keystrokes
 * run ahead of the debounced reload, and the rows answer the search that was actually applied.
 */
export interface AppliedDraftsQuery {
  /**
   * The fetch that committed these rows. A load-more continues this query only while this is still
   * the store's `_generation`: any newer fetch (a search, a status filter, a refresh or `reset()`)
   * retires it, so a page can never be appended into a list built from a different query.
   */
  generation: number;
  /** The filter-safe search term; "" when the rows are not a search. */
  search: string;
  /** The analysis-status filter these rows were loaded with; null when unfiltered. */
  status: string | null;
}

/**
 * PostgREST `.or()` filter for a drafts search: the same two columns `search_drafts_ranked`
 * matches on, with the same literal escaping, so the ranked page, the count and the plain list
 * query all select the same rows.
 */
function draftSearchOrFilter(term: string): string {
  const pattern = ilikeContainsPattern(term);
  return `title.ilike.${pattern},artist.ilike.${pattern}`;
}

/**
 * Does a row match the search the way the database does? The frontend mirror of
 * `public.search_like_pattern`: `\`, `%` and `_` match literally and `*` matches exactly one
 * character. A realtime row is then judged exactly as the query that fetched the rows around it.
 */
function draftMatchesSearch(
  row: { title?: string | null; artist?: string | null },
  term: string
): boolean {
  const pattern = term.replace(/[\\^$.*+?()[\]{}|]/g, (ch) =>
    ch === "*" ? "." : `\\${ch}`
  );
  const matcher = new RegExp(pattern, "i");
  return matcher.test(row.title || "") || matcher.test(row.artist || "");
}

interface DraftFilters<Q> {
  eq(column: "label_tag" | "analysis_status", value: string): Q;
  eq(column: "is_published", value: boolean): Q;
  is(column: "parent_track_id", value: null): Q;
  or(filter: string): Q;
}

/**
 * Which drafts match: the caller's label, unpublished MAIN WORKS that are not song-request
 * placeholders, the optional analysis-status filter and the search. Shared by the plain list query
 * and by the ranked path's count, so the total never disagrees with the list.
 */
function filterDrafts<Q extends DraftFilters<Q>>(
  query: Q,
  labelTag: string,
  applied: AppliedDraftsQuery
): Q {
  let filtered = query
    .eq("label_tag", labelTag) // Label-scoped: all label members see all drafts
    .eq("is_published", false) // Only fetch drafts
    .or("origin.is.null,origin.neq.license_request") // Hide song-request placeholders, keep NULL-origin drafts
    // Version families collapse: the list shows MAIN WORKS ONLY.
    // Children load lazily per page via fetchVersionsForParents().
    .is("parent_track_id", null);

  if (applied.search) {
    filtered = filtered.or(draftSearchOrFilter(applied.search));
  }
  if (applied.status) {
    filtered = filtered.eq("analysis_status", applied.status);
  }
  return filtered;
}

/** Where the next page starts: a keyset cursor for the unsearched list, an offset for the ranked one. */
interface DraftsPagePosition {
  cursor: string | null;
  offset: number;
}

/** The compound cursor `{ t: updated_at, id: last_id }`, or a legacy plain timestamp. */
function parseDraftCursor(cursor: string | null): { t: string | null; id: string | null } {
  if (!cursor) return { t: null, id: null };
  try {
    const parsed = JSON.parse(cursor);
    return { t: parsed.t ?? null, id: parsed.id ?? null };
  } catch {
    // Legacy plain timestamp cursor — treat as time-only
    return { t: cursor, id: null };
  }
}

/** One page of the unsearched list: newest first, keyset-paged by (updated_at, id). */
function cursorDraftsPage(
  labelTag: string,
  applied: AppliedDraftsQuery,
  cursor: string | null,
  pageSize: number,
  select: string
) {
  let query = filterDrafts(
    supabase.from("tracks").select(select, { count: "exact" }),
    labelTag,
    applied
  )
    .order("updated_at", { ascending: false })
    .order("id", { ascending: false }) // Tiebreaker for compound cursor pagination
    .limit(pageSize);

  // Rows after the cursor position:
  // WHERE updated_at < cursorTime OR (updated_at = cursorTime AND id < cursorId)
  const { t: cursorTime, id: cursorId } = parseDraftCursor(cursor);
  if (cursorTime && cursorId) {
    query = query.or(
      `updated_at.lt.${cursorTime},and(updated_at.eq.${cursorTime},id.lt.${cursorId})`
    );
  } else if (cursorTime) {
    query = query.lt("updated_at", cursorTime);
  }
  return query;
}

/**
 * One page of drafts, from whichever query the applied search calls for.
 *
 * SEARCHED — the database ranks the matches (exact > starts with > a word starts with > contains)
 * in `search_drafts_ranked`. That order has no keyset, so this path pages by OFFSET, and its total
 * is a separate count over the same filters: a GET limited to one row, never a HEAD request, which
 * drew Supabase gateway 503s (see TracksPage.fetchCounts). The function returns bare track rows,
 * so the uploader's name comes from the profiles batch — exactly as it does whenever the embed
 * below is unavailable.
 *
 * UNSEARCHED — newest first, keyset-paged by (updated_at, id) and counted by the page query itself.
 *
 * Supabase errors are thrown so the caller writes one error state for either path.
 */
async function fetchDraftsPage(
  labelTag: string,
  applied: AppliedDraftsQuery,
  position: DraftsPagePosition,
  pageSize: number
): Promise<{ data: DraftTrackRow[] | null; count: number | null }> {
  if (applied.search) {
    const [page, total] = await Promise.all([
      supabase.rpc("search_drafts_ranked", {
        p_label_tag: labelTag,
        p_search: applied.search,
        // An omitted argument takes the function's default: no status filter.
        p_analysis_status: applied.status ?? undefined,
        p_limit: pageSize,
        p_offset: position.offset,
      }),
      filterDrafts(
        supabase.from("tracks").select("id", { count: "exact" }).limit(1),
        labelTag,
        applied
      ),
    ]);
    if (page.error) throw page.error;
    if (total.error) throw total.error;
    return { data: page.data as DraftTrackRow[] | null, count: total.count };
  }

  let result = await cursorDraftsPage(
    labelTag,
    applied,
    position.cursor,
    pageSize,
    DRAFT_SELECT_WITH_UPLOADER
  );
  // If the profiles relationship is unavailable, fetch the page without the join; the mapper
  // backfills display_name from the profiles batch below.
  if (
    result.error &&
    (result.error.message?.includes("foreign key") ||
      result.error.message?.includes("relation"))
  ) {
    result = await cursorDraftsPage(labelTag, applied, position.cursor, pageSize, "*");
  }
  if (result.error) throw result.error;
  // The select is chosen at runtime (with the embed or without it), so PostgREST cannot type the
  // row; either way it is a tracks row, carrying the uploader embed when the join was available.
  return { data: result.data as unknown as DraftTrackRow[] | null, count: result.count };
}

/** A page's rows as Drafts: one batched profiles lookup for the page, then the shared mapper. */
async function mapDraftRows(rows: DraftTrackRow[]): Promise<Draft[]> {
  const userIds = rows
    .map((track) => track.user_id)
    .filter((id): id is string => Boolean(id));
  const profilesMap = await UserCacheService.getProfilesBatch(userIds);
  return Promise.all(
    rows.map((track) => mapSupabaseTrackToDraft(track, profilesMap))
  );
}

/**
 * What a delete does to the track's VERSIONS (decision 8).
 *
 * `"detach"` is what this endpoint has always done — the FK is ON DELETE SET NULL, so the versions
 * survive as main tracks of their own. `"delete"` takes them too, live ones included. The server
 * expands the family itself: a client-built id list would miss the live versions, which the drafts
 * query never loads.
 */
export type DeleteVersionsChoice = "detach" | "delete";

export interface DeleteDraftOptions {
  versions: DeleteVersionsChoice;
  /** Every version of the track, draft and live, as the confirm counted them. */
  expectedVersions?: number;
}

export interface DeleteDraftResult {
  success: boolean;
  error?: string;
  /** The server's refusal code when it sent one — `family_changed` is worth asking again about. */
  reason?: TrackFamilyReason;
}

interface DraftsState {
  /** Wipe draft data + selection + pagination (logout/account switch). Data only — the realtime channel is component-owned. */
  reset: () => void;
  drafts: Draft[];
  selectedDraftId: string | null;
  isDetailsOpen: boolean;
  isSelectionMode: boolean;
  selectedDraftIds: Set<string>;
  isLoading: boolean;
  isFetchingMore: boolean;
  loadedOnce: boolean;
  cursor: string | null;
  /** Rows already taken from the applied query — the next ranked page's `p_offset`. */
  nextOffset: number;
  hasMore: boolean;
  totalCount: number;
  error: string | null;
  recentlyFailedIds: string[];
  initialTab: string | null;
  searchQuery: string;
  statusFilter: string | null;
  /**
   * Ids this client has just acted on. A realtime echo of one of these is this client's own
   * work coming back, so the row leaves the list silently. Entries expire by themselves after
   * `OWN_OP_ECHO_MS`.
   */
  ownPendingOps: Set<string>;
  /** What the drafts on screen were loaded with. `null` until the first fetch commits, and after `reset()`. */
  appliedQuery: AppliedDraftsQuery | null;
  _generation: number;

  setSearchQuery: (query: string) => void;
  setStatusFilter: (status: string | null) => void;
  setRecentlyFailed: (ids: string[]) => void;
  markOwnPendingOps: (ids: string[]) => void;
  patchVersion: (parentId: string, versionId: string, patch: Partial<Draft>) => void;
  refreshVersions: (parentId: string) => Promise<void>;
  attachVersion: (
    mainId: string,
    versionId: string,
    input: {
      versionType: string;
      editType: string | null;
      expectedVersions: number;
    }
  ) => Promise<AttachVersionResult>;
  detachVersion: (parentId: string, versionId: string) => Promise<void>;
  fetchDrafts: () => Promise<void>;
  fetchMoreDrafts: () => Promise<void>;
  addDraft: (draft: Draft) => void;
  subscribeToChanges: () => () => void;
  selectDraft: (id: string) => void;
  openDraftToTab: (id: string, tab: string) => void;
  closeDetails: () => void;
  toggleSelectionMode: () => void;
  toggleDraftSelection: (id: string) => void;
  selectAllDrafts: () => void;
  clearSelection: () => void;
  updateDraft: (id: string, updates: Partial<Draft>) => Promise<void>;
  publishDraft: (id: string) => Promise<void>;
  publishMultipleDrafts: (ids: string[]) => Promise<{
    success: boolean;
    published: string[];
    failed: string[];
    errors: string[];
    /** `reason` is the server's refusal code when there was one — `parent_no_tier` has a fix. */
    failedDetails: Array<{ draft: Draft; error: string; reason?: string }>;
  }>;
  deleteDraft: (
    id: string,
    options?: DeleteDraftOptions
  ) => Promise<DeleteDraftResult>;
  resolveMetadataConflict: (id: string, source: "acr" | "embedded") => Promise<{ success: boolean; error?: string }>;
}

let recentlyFailedTimeout: ReturnType<typeof setTimeout> | null = null;

/** Per-id expiry of `ownPendingOps`, and per-parent debounce of the version refetch. */
const ownOpTimers = new Map<string, ReturnType<typeof setTimeout>>();
const versionsRefetchTimers = new Map<string, ReturnType<typeof setTimeout>>();

function clearTimers(timers: Map<string, ReturnType<typeof setTimeout>>) {
  for (const timer of timers.values()) clearTimeout(timer);
  timers.clear();
}

function forgetOwnPendingOp(id: string) {
  ownOpTimers.delete(id);
  useDraftsStore.setState((state) => {
    if (!state.ownPendingOps.has(id)) return {};
    const ownPendingOps = new Set(state.ownPendingOps);
    ownPendingOps.delete(id);
    return { ownPendingOps };
  });
}

/**
 * Re-read one parent's versions now. Nothing happens when the parent is not on screen: a family
 * the user has not paged to has nowhere to land.
 */
async function refreshParentVersions(parentId: string) {
  if (!useDraftsStore.getState().drafts.some((d) => d.id === parentId)) return;

  const fresh = await fetchVersionsForParents([parentId]);
  // A read that failed is not a family with no versions. Writing its zeros in would take the
  // "+N versions" badge off a loaded row and empty the Versions tab under a seller who is being
  // told by `getFamily`, in the same breath, that there are three — with nothing on screen to
  // explain it and no way back but a full refetch. The family already on screen is older, but
  // it is the truth as far as anything here knows.
  if (!fresh.ok) return;

  useDraftsStore.setState((state) => ({
    drafts: state.drafts.map((d) =>
      d.id === parentId
        ? {
            ...d,
            versions: fresh.byParent.get(parentId) ?? [],
            version_count: fresh.counts.get(parentId) ?? 0,
          }
        : d
    ),
  }));
}

/**
 * Re-read one parent's versions, at most once per `VERSIONS_REFETCH_DEBOUNCE_MS`.
 *
 * The cheap path for a version change is a merge into the loaded row (`patchVersion`). This is
 * the fallback for the changes a merge cannot express: a row that joined the family, left it,
 * or went live. This client's OWN operations call `refreshParentVersions` directly — they know
 * exactly one family changed, and the seller is looking at it.
 */
function scheduleVersionsRefetch(parentId: string) {
  const running = versionsRefetchTimers.get(parentId);
  if (running) clearTimeout(running);

  versionsRefetchTimers.set(
    parentId,
    setTimeout(() => {
      versionsRefetchTimers.delete(parentId);
      void refreshParentVersions(parentId);
    }, VERSIONS_REFETCH_DEBOUNCE_MS)
  );
}

/**
 * Does a row belong in the list on screen?
 *
 * Judged against the query those rows answer — the applied term and status filter, matched the way
 * the database matches them — not the keystrokes in the search box, which run ahead of the reload.
 */
function matchesAppliedQuery(
  applied: AppliedDraftsQuery | null,
  candidate: {
    title?: string | null;
    artist?: string | null;
    analysis_status?: string | null;
  }
): boolean {
  if (!applied) return true;
  if (applied.status && candidate.analysis_status !== applied.status) return false;
  if (applied.search && !draftMatchesSearch(candidate, applied.search)) return false;
  return true;
}

/**
 * A realtime row image. Postgres sends the whole new row, minus any TOASTed column that did not
 * change, so every field has to be treated as possibly absent.
 */
type RealtimeTrackRow = Partial<Tables<"tracks">> & { id: string };

/**
 * What a version row's realtime payload is allowed to change in the copy held on screen.
 *
 * Everything else the seller sees on a version row — the title bar, the play button — comes from
 * the row loaded with its family, and an absent key in the payload means "unchanged", never
 * "cleared".
 */
function versionPatchFromPayload(row: RealtimeTrackRow): Partial<Draft> {
  const patch: Partial<Draft> = {};
  if (row.title !== undefined) patch.title = row.title;
  if (row.analysis_status !== undefined) {
    patch.analysis_status = (row.analysis_status ??
      undefined) as Draft["analysis_status"];
  }
  if (row.error_message !== undefined) patch.error_message = row.error_message ?? undefined;
  if (row.version_type !== undefined) patch.version_type = row.version_type;
  if (row.edit_type !== undefined) patch.edit_type = row.edit_type;
  return patch;
}

/**
 * The state change for a row leaving the drafts list — published, attached to another track,
 * or deleted.
 *
 * `totalCount` follows: the exact-count query counts this label's unpublished MAIN WORKS, so a
 * row that leaves is one fewer. It also leaves the selection and, if it is the open one, the
 * sheet — a sheet whose row is gone renders nothing behind its own backdrop.
 */
function withoutDraft(state: DraftsState, id: string): Partial<DraftsState> {
  if (!state.drafts.some((d) => d.id === id)) return {};
  const wasOpen = state.selectedDraftId === id;
  const selectedDraftIds = state.selectedDraftIds.has(id)
    ? new Set([...state.selectedDraftIds].filter((selected) => selected !== id))
    : state.selectedDraftIds;

  return {
    drafts: state.drafts.filter((d) => d.id !== id),
    totalCount: Math.max(0, state.totalCount - 1),
    selectedDraftIds,
    selectedDraftId: wasOpen ? null : state.selectedDraftId,
    isDetailsOpen: wasOpen ? false : state.isDetailsOpen,
    initialTab: wasOpen ? null : state.initialTab,
  };
}

/**
 * The list with one version dropped from a loaded family — detached, or deleted.
 *
 * The parent keeps its place; only its `versions[]` and the badge beside it move. A version was
 * never a row of this list, so `totalCount` is not this function's business.
 */
function withoutVersion(
  state: DraftsState,
  parentId: string,
  versionId: string
): Draft[] {
  return state.drafts.map((d) =>
    d.id === parentId
      ? {
          ...d,
          versions: (d.versions ?? []).filter((v) => v.id !== versionId),
          version_count: Math.max(0, (d.version_count ?? 0) - 1),
        }
      : d
  );
}

/**
 * The state change for a deleted main track, together with the versions its deletion freed.
 *
 * Under `versions: "detach"` the FK turns every version into a main track — a row of THIS list —
 * so they are put in rather than waited for. Only the family's DRAFT half can be: a live version
 * is a catalogue row that the drafts query never loaded, so there is nothing to insert and, since
 * `totalCount` counts unpublished main works, nothing to count either. A promoted row still has to
 * answer the query on screen, the same rule a detach follows.
 */
function withDeletedDraft(
  state: DraftsState,
  id: string,
  nowMainIds: string[]
): Partial<DraftsState> {
  const parent = state.drafts.find((d) => d.id === id);
  const gone = withoutDraft(state, id);
  if (!parent || nowMainIds.length === 0) return gone;

  const freed = new Set(nowMainIds);
  const promoted = (parent.versions ?? [])
    .filter(
      (version) =>
        freed.has(version.id) && matchesAppliedQuery(state.appliedQuery, version)
    )
    .map((version) => ({
      ...version,
      parent_track_id: null,
      version_count: 0,
      versions: [],
    }));
  if (promoted.length === 0) return gone;

  return {
    ...gone,
    // Orphaning bumps `updated_at`, so the top is where the list's own order puts them.
    drafts: [...promoted, ...(gone.drafts ?? state.drafts)],
    totalCount: (gone.totalCount ?? state.totalCount) + promoted.length,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Publishing — the parent's own row is written here, the family flip is the server's
// ─────────────────────────────────────────────────────────────────────────────

/** What one family's publish actually did, told in the numbers a seller is shown. */
interface FamilyPublishOutcome {
  mainId: string;
  published: boolean;
  /** Versions that went live with the main. */
  versions: number;
  /** Versions that are live but have no search entry because their embedding failed. */
  embedFailed: number;
  error: { reason: string; message: string } | null;
}

function outcomeFromResult(result: PublishFamilyResult): FamilyPublishOutcome {
  return {
    mainId: result.main_id,
    published: !result.error,
    versions: result.published_ids.filter((id) => id !== result.main_id).length,
    embedFailed: result.skipped_search.filter((s) => s.why === "embed_failed").length,
    error: result.error,
  };
}

/**
 * Publish these families, and never report a lie when the request itself went missing.
 *
 * A dropped connection is UNKNOWN, not failed: the flip may well have happened on the server. A
 * client that called it a failure would send the seller to press Publish again on a track that is
 * already live, and the second attempt is refused with "This track is already live." — a rule
 * they did not break.
 */
async function runFamilyPublish(mainIds: string[]): Promise<FamilyPublishOutcome[]> {
  try {
    const { results } = await publishFamilies(mainIds);
    return results.map(outcomeFromResult);
  } catch (error) {
    if (!(error instanceof TrackFamilyRefusal) || error.reason !== "unknown") throw error;
    return confirmPublishAfterTimeout(mainIds, error);
  }
}

/**
 * Read the truth back: which of these mains are live now?
 *
 * `is_published` comes from the row itself — the preview endpoint counts a family but does not
 * say whether its main went live, and that is the one fact this path turns on. The preview is
 * then asked for the version count, so a recovered publish reports the same numbers a normal one
 * would. If the read itself cannot be made, the original "we don't know" stands.
 */
async function confirmPublishAfterTimeout(
  mainIds: string[],
  cause: TrackFamilyRefusal
): Promise<FamilyPublishOutcome[]> {
  const { data, error } = await supabase
    .from("tracks")
    .select("id, is_published")
    .in("id", mainIds);
  if (error || !data) throw cause;

  const live = new Set(
    data.filter((row) => row.is_published).map((row) => String(row.id))
  );

  const outcomes: FamilyPublishOutcome[] = [];
  for (const mainId of mainIds) {
    if (!live.has(mainId)) {
      outcomes.push({
        mainId,
        published: false,
        versions: 0,
        embedFailed: 0,
        error: { reason: cause.reason, message: cause.message },
      });
      continue;
    }
    let versions = 0;
    try {
      versions = (await getFamily(mainId)).live_versions;
    } catch {
      // The count is a nicety; that the track is live is the fact, and it is already known.
    }
    outcomes.push({ mainId, published: true, versions, embedFailed: 0, error: null });
  }
  return outcomes;
}

const plural = (count: number, noun: string) =>
  `${count} ${count === 1 ? noun : `${noun}s`}`;

/**
 * What went live, and what went live without being findable.
 *
 * Two toasts at most, and the second one only for an embedding that failed: a version skipped
 * because it has not been analysed was already named in the confirm dialog, and repeating it
 * after the fact would read as an error when it is the design (decision 4).
 */
function announcePublished(outcomes: FamilyPublishOutcome[]) {
  const published = outcomes.filter((o) => o.published);
  if (published.length === 0) return;

  const versions = published.reduce((sum, o) => sum + o.versions, 0);
  toast.success(
    versions > 0
      ? `Published ${plural(published.length, "track")} and ${plural(versions, "version")}`
      : `Published ${plural(published.length, "track")}`
  );

  const embedFailed = published.reduce((sum, o) => sum + o.embedFailed, 0);
  if (embedFailed > 0) {
    toast.error(
      `${plural(embedFailed, "version")} ${embedFailed === 1 ? "is" : "are"} live ` +
        "but could not be added to search. " +
        "Move the track to drafts and publish again."
    );
  }
}

/**
 * Write the parent's edited metadata and its licensing onto the row, before anything goes live.
 *
 * `is_published` and `analysis_status` are NOT in this payload and must never be: publication is
 * the family endpoint's to decide, and the analysis column belongs to the analysis queue — a
 * client that rewrote it from memory could hand itself the publish gate it had just failed.
 */
async function patchParentForPublish(draft: Draft): Promise<void> {
  const selectLyricsPayload = (
    ...candidates: Array<Draft["lyrics"] | undefined>
  ) => {
    for (const candidate of candidates) {
      if (!candidate) continue;
      if (typeof candidate === "string") {
        if (candidate.trim()) return candidate;
        continue;
      }
      const body =
        typeof (candidate as any).body === "string"
          ? (candidate as any).body.trim()
          : "";
      const content =
        typeof (candidate as any).content === "string"
          ? (candidate as any).content.trim()
          : "";
      const text =
        typeof (candidate as any).text === "string"
          ? (candidate as any).text.trim()
          : "";

      if (body || content || text) {
        return candidate;
      }
    }
    return null;
  };

  // Get existing licensing configuration from the draft (if any)
  const existingConfig = await TrackLicensingService.getLicensingConfiguration(
    draft.id
  );

  // Extract metadata from the draft (if it exists)
  const metadata = draft.metadata || {};

  // Build a payload with valid track table fields (id, title, artist, bpm, etc.)
  // plus new fields like user_id, cover_art_url, album, isrc.
  const trackData: { [key: string]: any } = {
    // Core fields that exist in tracks table
    title: draft.title,
    artist: draft.artist,
    bpm: metadata.bpm,
    tempo: metadata.tempo || metadata.bpm, // Include tempo field, fallback to bpm
    key: metadata.key,
    genre:
      typeof metadata.genre === "string" ? [metadata.genre] : metadata.genre,
    moods: metadata.moods,
    instruments: metadata.instruments,
    vocal_gender: metadata.vocal_gender,
    vocal_type: metadata.vocal_type,
    description: metadata.description || (draft as any).description || null,
    duration: metadata.duration,
    explicit_content: metadata.explicit_content,
    language: metadata.language,
    gemini_mood: (draft.metadata as any)?.gemini_mood,
    gemini_tags: (draft.metadata as any)?.gemini_tags,

    cover_art_url: draft.cover_art_url,
    artwork_url: draft.cover_art_url,

    // ACRCloud metadata (access safely with optional chaining)
    album: (metadata as any)?.album,
    isrc: (metadata as any)?.isrc,
    upc: (metadata as any)?.upc,
    release_date: (metadata as any)?.releaseDate,

    // Extended Gemini analysis
    emotional_arc: metadata.emotional_arc,
    harmony: metadata.harmony,
    chord_progression: metadata.chord_progression,
    lyrical_theme: metadata.lyrical_theme,
    cultural_fusion: metadata.cultural_fusion,
    historical_period: metadata.historical_period,
    subgenre: metadata.subgenre,

    // UI fields
    rights: draft.rights,
    tags: draft.tags,
    status: draft.status,

    // LICENSING CONFIGURATION: Only use existing licensing data. The family endpoint copies
    // these columns from this row onto every version, so they have to be on it first.
    licensing_tier: existingConfig?.licensing_tier || draft.licensing?.tier,
    pricing_method: existingConfig?.pricing_method,
    fixed_price: existingConfig?.fixed_price,
    min_price: existingConfig?.min_price,
    max_price: existingConfig?.max_price,
    sweet_spot_price: existingConfig?.sweet_spot_price,
    allowed_usage_types: existingConfig?.allowed_usage_types || null,
    licensing_restrictions: existingConfig?.licensing_restrictions,

    // Timestamps
    updated_at: new Date().toISOString(),
  };

  // Remove undefined values (null is intentional — means "clear this field")
  Object.keys(trackData).forEach((key) => {
    if (trackData[key] === undefined) {
      delete trackData[key];
    }
  });

  const lyricsPayload = selectLyricsPayload(
    draft.lyrics,
    draft.metadata?.lyrics as Draft["lyrics"]
  );
  if (lyricsPayload) {
    trackData.lyrics = lyricsPayload;
  }

  // FINAL SAFETY CHECK: Ensure genre is always an array if it exists
  if (trackData.genre && typeof trackData.genre === "string") {
    trackData.genre = [trackData.genre];
  }

  const { error } = await supabase
    .from("tracks")
    .update(trackData)
    .eq("id", draft.id)
    .select()
    .single();

  if (error) {
    console.error("[Publish] Saving the track before publishing failed:", error);
    throw new Error(`Database update failed: ${error.message}`);
  }
}

export const useDraftsStore = create<DraftsState>((set, get) => ({
  drafts: [],
  selectedDraftId: null,
  isDetailsOpen: false,
  isSelectionMode: false,
  selectedDraftIds: new Set(),
  isLoading: true,
  isFetchingMore: false,
  loadedOnce: false,
  cursor: null,
  nextOffset: 0,
  hasMore: true,
  totalCount: 0,
  error: null,
  recentlyFailedIds: [],
  initialTab: null,
  searchQuery: "",
  statusFilter: null,
  ownPendingOps: new Set(),
  appliedQuery: null,
  _generation: 0,

  reset: () => {
    clearTimers(ownOpTimers);
    clearTimers(versionsRefetchTimers);
    set((state) => ({
      drafts: [],
      selectedDraftId: null,
      isDetailsOpen: false,
      isSelectionMode: false,
      selectedDraftIds: new Set<string>(),
      isLoading: true,
      isFetchingMore: false,
      loadedOnce: false,
      cursor: null,
      nextOffset: 0,
      hasMore: true,
      totalCount: 0,
      error: null,
      recentlyFailedIds: [],
      initialTab: null,
      searchQuery: "",
      statusFilter: null,
      ownPendingOps: new Set<string>(),
      appliedQuery: null,
      _generation: state._generation + 1,
    }));
  },

  setSearchQuery: (query: string) => {
    if (query === get().searchQuery) return;
    set({ searchQuery: query, cursor: null, nextOffset: 0, hasMore: false });
    get().fetchDrafts();
  },

  setStatusFilter: (status: string | null) => {
    if (status === get().statusFilter) return;
    set({ statusFilter: status, cursor: null, nextOffset: 0, hasMore: false });
    get().fetchDrafts();
  },

  setRecentlyFailed: (ids: string[]) => {
    if (recentlyFailedTimeout) clearTimeout(recentlyFailedTimeout);
    set({ recentlyFailedIds: ids });
    recentlyFailedTimeout = setTimeout(() => {
      set({ recentlyFailedIds: [] });
      recentlyFailedTimeout = null;
    }, 18000);
  },

  /**
   * Remember, briefly, that this client is acting on these ids, so the realtime echo of its own
   * work arrives silently. Call it BEFORE the write: the echo can land while the request is
   * still in flight.
   */
  markOwnPendingOps: (ids: string[]) => {
    if (ids.length === 0) return;
    for (const id of ids) {
      const running = ownOpTimers.get(id);
      if (running) clearTimeout(running);
      ownOpTimers.set(id, setTimeout(() => forgetOwnPendingOp(id), OWN_OP_ECHO_MS));
    }
    set((state) => {
      const ownPendingOps = new Set(state.ownPendingOps);
      for (const id of ids) ownPendingOps.add(id);
      return { ownPendingOps };
    });
  },

  /**
   * Merge a change into one loaded version row.
   *
   * A version is not a row of the drafts list — it lives inside its parent's `versions[]` — so
   * `updateDraft`'s top-level map never reaches it. Realtime and the analysis retry both write
   * through here. Unknown parent or version: nothing happens, and the caller's own refetch is
   * what will show the row.
   */
  patchVersion: (parentId: string, versionId: string, patch: Partial<Draft>) =>
    set((state) => {
      const parent = state.drafts.find((d) => d.id === parentId);
      if (!parent?.versions?.some((v) => v.id === versionId)) return {};
      return {
        drafts: state.drafts.map((d) =>
          d.id === parentId
            ? {
                ...d,
                versions: (d.versions ?? []).map((v) =>
                  v.id === versionId ? { ...v, ...patch } : v
                ),
              }
            : d
        ),
      };
    }),

  refreshVersions: async (parentId: string) => refreshParentVersions(parentId),

  /**
   * Attach a draft to a main track as a version — this client's own operation, so the list is
   * corrected here rather than waited for.
   *
   * The attached track stops being a main work, so it leaves the list and takes the count with it;
   * its own versions were never rows of the list (they hang off it), so they leave with it too.
   * `markOwnPendingOps` is claimed BEFORE the request: realtime can deliver the re-parenting while
   * the response is still in flight, and the seller who pressed Attach must not be told a
   * colleague moved their track. Refusals are thrown for the dialog to render against its fields.
   */
  attachVersion: async (mainId, versionId, input) => {
    get().markOwnPendingOps([versionId]);
    const result = await attachVersionRequest({
      mainId,
      versionId,
      versionType: input.versionType,
      editType: input.editType,
      expectedVersions: input.expectedVersions,
    });
    // A no-op when the echo already removed the row, so `totalCount` drops exactly once.
    set((state) => withoutDraft(state, versionId));
    await refreshParentVersions(mainId);
    return result;
  },

  /**
   * Detach a version: it becomes a main track again and belongs back in the list.
   *
   * The server returns the whole row, so this needs no second read — but a row only belongs in
   * THIS list when it answers the query on screen. Detaching under an active search that it does
   * not match leaves the family correct and the list untouched, count included.
   */
  detachVersion: async (parentId, versionId) => {
    get().markOwnPendingOps([versionId]);
    const { track } = await detachVersionRequest(versionId);
    const detached = await mapSupabaseTrackToDraft(track);

    set((state) => {
      const drafts = withoutVersion(state, parentId, versionId);

      if (
        drafts.some((d) => d.id === versionId) ||
        !matchesAppliedQuery(state.appliedQuery, track)
      ) {
        return { drafts };
      }
      return {
        // Detaching bumped `updated_at`, so the top is where the list's own order puts it.
        drafts: [{ ...detached, parent_track_id: null, version_count: 0, versions: [] }, ...drafts],
        totalCount: state.totalCount + 1,
      };
    });
  },

  fetchDrafts: async () => {
    const gen = get()._generation;
    // Only show full-page loading spinner on initial load, not on search refetches
    const isInitialLoad = !get().loadedOnce;
    set({ isLoading: isInitialLoad, error: null, _generation: gen + 1 });
    const currentGen = gen + 1;
    // The query this page answers, committed with its rows below. `toOrFilterSearchTerm` is used
    // for both paths so the ranked page, its count and the plain query all search the same text.
    const query: AppliedDraftsQuery = {
      generation: currentGen,
      search: toOrFilterSearchTerm(get().searchQuery),
      status: get().statusFilter,
    };
    try {
      // Use cached user data to prevent duplicate auth calls
      const user = await UserCacheService.getUser();

      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      // Get user's label_tag for label-scoped drafts
      const labelTag = await LabelCacheService.getLabelTag(user.id);

      // Users without a label see no drafts (they need to join a label first)
      if (!labelTag) {
        if (get()._generation !== currentGen) return;
        set({
          drafts: [],
          isLoading: false,
          isFetchingMore: false,
          loadedOnce: true,
          cursor: null,
          nextOffset: 0,
          hasMore: false,
          totalCount: 0,
          appliedQuery: query,
        });
        return;
      }

      const { data, count } = await fetchDraftsPage(
        labelTag,
        query,
        { cursor: null, offset: 0 },
        PAGE_SIZE
      );

      if (!data) {
        throw new Error("No data returned from query");
      }

      // Discard results if a newer fetchDrafts has started since this one began
      if (get()._generation !== currentGen) return;

      const mappedDrafts = await mapDraftRows(data);

      // One batched query per page attaches every version family on this page.
      const versionsForPage = await fetchVersionsForParents(
        mappedDrafts.map((d) => d.id)
      );
      const draftsWithVersions = attachVersions(mappedDrafts, versionsForPage);

      // Check again after profile fetch
      if (get()._generation !== currentGen) return;

      const last = data[data.length - 1];
      // New rows and the query they answer land together: nothing can read one without the other.
      set({
        drafts: draftsWithVersions,
        isLoading: false,
        isFetchingMore: false,
        loadedOnce: true,
        cursor: last ? JSON.stringify({ t: last.updated_at, id: last.id }) : null,
        nextOffset: data.length,
        hasMore: data.length === PAGE_SIZE,
        totalCount: typeof count === "number" ? count : data.length,
        appliedQuery: query,
      });
    } catch (error: any) {
      // Don't write error if a newer fetch has superseded this one
      if (get()._generation !== currentGen) return;
      console.error("Failed to fetch drafts:", error);
      set({ error: error.message, isLoading: false });
    }
  },

  fetchMoreDrafts: async () => {
    const {
      appliedQuery,
      cursor,
      nextOffset,
      hasMore,
      isFetchingMore,
      _generation: gen,
    } = get();
    if (!hasMore || isFetchingMore) return;
    // A load-more continues the drafts ON SCREEN — the term, status filter and paging mode they
    // were loaded with — never the search still being typed. Before the first commit, or once a
    // newer fetch is under way, there is nothing to continue.
    if (!appliedQuery || appliedQuery.generation !== gen) return;

    set({ isFetchingMore: true, error: null });
    try {
      const user = await UserCacheService.getUser();
      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      // Get user's label_tag for label-scoped drafts
      const labelTag = await LabelCacheService.getLabelTag(user.id);
      if (!labelTag) {
        if (get()._generation !== gen) {
        // A newer fetch superseded this page. Clear the in-flight flag anyway —
        // leaving it true permanently blocks loadMore's entry guard and kills scroll.
        set({ isFetchingMore: false });
        return;
      }
        set({ isFetchingMore: false, hasMore: false });
        return;
      }

      const { data, count } = await fetchDraftsPage(
        labelTag,
        appliedQuery,
        { cursor, offset: nextOffset },
        PAGE_SIZE
      );

      if (!data) {
        set({ isFetchingMore: false, hasMore: false });
        return;
      }

      // Discard results if a newer fetchDrafts has started since this fetch began
      if (get()._generation !== gen) {
        // A newer fetch superseded this page. Clear the in-flight flag anyway —
        // leaving it true permanently blocks loadMore's entry guard and kills scroll.
        set({ isFetchingMore: false });
        return;
      }

      // Map tracks to drafts using one batched profiles lookup
      const mappedDrafts = await mapDraftRows(data);

      // Check again after profile fetch — before paying for the version query.
      if (get()._generation !== gen) {
        // A newer fetch superseded this page. Clear the in-flight flag anyway —
        // leaving it true permanently blocks loadMore's entry guard and kills scroll.
        set({ isFetchingMore: false });
        return;
      }

      // One batched query per page attaches every version family on this page.
      const versionsForPage = await fetchVersionsForParents(
        mappedDrafts.map((d) => d.id)
      );
      const draftsWithVersions = attachVersions(mappedDrafts, versionsForPage);

      const last = data[data.length - 1];
      set((state) => {
        const nextTotal =
          typeof count === "number" && count > 0
            ? count
            : state.totalCount;
        // Deduplicate: filter out any drafts already in the list (safety net for realtime races)
        const existingIds = new Set(state.drafts.map((d) => d.id));
        const newDrafts = draftsWithVersions.filter((d) => !existingIds.has(d.id));
        return {
          drafts: [...state.drafts, ...newDrafts],
          cursor: last ? JSON.stringify({ t: last.updated_at, id: last.id }) : state.cursor,
          // The offset counts rows the database returned, not rows kept: dedupe must not
          // make the next ranked page re-read what this one already consumed.
          nextOffset: state.nextOffset + data.length,
          hasMore: data.length === PAGE_SIZE,
          isFetchingMore: false,
          totalCount: Math.max(state.totalCount, nextTotal),
        };
      });
    } catch (error: any) {
      // Don't write error if a newer fetch has superseded this one
      if (get()._generation !== gen) {
        // A newer fetch superseded this page. Clear the in-flight flag anyway —
        // leaving it true permanently blocks loadMore's entry guard and kills scroll.
        set({ isFetchingMore: false });
        return;
      }
      console.error("Failed to fetch more drafts:", error);
      set({ error: error.message, isFetchingMore: false });
    }
  },

  addDraft: (draft: Draft) => {
    set((state) => ({
      drafts: [draft, ...state.drafts],
    }));
  },

  subscribeToChanges: () => {
    let channel: any;
    let cancelled = false;
    let retryCount = 0;
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 5000;

    /**
     * A row that just left the drafts list, and what the seller is told about it.
     *
     * The toast speaks only for the track they have open — that sheet is closing under them,
     * and "filtered" is not news at all: the row only stopped matching the search or status
     * filter on screen. An echo of this client's own operation is silent: it already reported
     * its own result.
     */
    const removeFromList = (
      id: string,
      reason: "published" | "deleted" | "attached" | "filtered"
    ) => {
      const before = get();
      if (!before.drafts.some((d) => d.id === id)) return;
      const wasOpen = before.selectedDraftId === id;

      set((state) => withoutDraft(state, id));

      if (!wasOpen || reason === "filtered" || before.ownPendingOps.has(id)) return;
      toast(
        reason === "attached"
          ? "This track is now a version of another track."
          : // Published and deleted read the same to the seller whose sheet just closed: the
            // track is not in Drafts any more, and where it went is not this toast's business.
            "This track is no longer in Drafts."
      );
    };

    /**
     * A row of this label that was deleted, by whoever deleted it.
     *
     * A DELETE payload carries the primary key alone, which is enough here and the only thing
     * that is: the row is touched only where this client already holds it, and everything it
     * holds is already label-scoped and filtered. Nothing is pulled in, nothing is re-read.
     */
    const removeDeleted = (id: string) => {
      if (get().drafts.some((d) => d.id === id)) {
        removeFromList(id, "deleted");
        return;
      }
      // Not a row of the list: it may be a VERSION of a family on screen, and dropping it is both
      // cheaper and more certain than re-reading a parent for a row that is known to be gone.
      const holderId = get().drafts.find((d) =>
        d.versions?.some((v) => v.id === id)
      )?.id;
      if (holderId) {
        set((state) => ({ drafts: withoutVersion(state, holderId, id) }));
      }
    };

    const setupSubscription = async () => {
      let labelTag: string | null = null;
      try {
        const user = await UserCacheService.getUser();
        if (!user?.id || cancelled) return;

        labelTag = await LabelCacheService.getLabelTag(user.id);
        if (!labelTag || cancelled) return;
      } catch (err) {
        console.warn("Drafts realtime: failed to resolve user/label, skipping subscription.", err);
        return;
      }

      const channelName = uniqueChannelTopic(`drafts-changes-${labelTag}`);

      channel = supabase
        .channel(channelName)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "tracks",
            filter: `label_tag=eq.${labelTag}`,
          },
          async (payload) => {
            // DELETE carries the primary key only (`tracks` has REPLICA IDENTITY DEFAULT), so it
            // is judged on the key alone — see `removeDeleted`. Leaving it unhandled left a
            // colleague's delete showing as a row that is gone, under a sheet whose every save
            // would fail against a row that no longer exists.
            if (payload.eventType === "DELETE") {
              const deletedId = (payload.old as { id?: string } | undefined)?.id;
              if (deletedId) removeDeleted(deletedId);
              return;
            }

            const eventType = payload.eventType;
            const row = payload.new as RealtimeTrackRow | undefined;
            if (!row?.id) return;
            // Label-scoped, and song-request placeholders are not drafts.
            if (row.label_tag !== labelTag) return;
            if (row.origin === "license_request") return;

            if (eventType === "INSERT") {
              // The list renders MAIN WORKS ONLY (the query filters parent_track_id IS NULL),
              // and drafts only. Realtime must honour the same rule or a live upload injects
              // version children as top-level rows.
              if (row.is_published !== false || row.parent_track_id != null) return;
              if (!matchesAppliedQuery(get().appliedQuery, row)) return;
              const mapped = await mapSupabaseTrackToDraft(row);
              set((state) => ({
                drafts: [mapped, ...state.drafts],
                totalCount: state.totalCount + 1,
              }));
              return;
            }

            if (eventType !== "UPDATE") return;

            if (!get().drafts.some((d) => d.id === row.id)) {
              // Not a row of the list. It may still be a VERSION of a family on screen, which
              // the list holds inside its parent rather than as a row of its own.
              const holderId =
                get().drafts.find((d) => d.versions?.some((v) => v.id === row.id))?.id ?? null;
              const parentId = row.parent_track_id ?? null;
              const parentLoaded =
                parentId != null && get().drafts.some((d) => d.id === parentId);

              // Neither: a row the user has not paged to, and an UPDATE is no reason to pull it
              // in. Prepending here is what made the drafts page unscrollable — the list is
              // cursor-paginated by updated_at DESC, so a row whose updated_at just changed
              // genuinely sorts to position 0, and Felt had ~17k tracks draining through
              // analysis, each completion teleporting a card the user had never seen to the top
              // and pushing the infinite-scroll sentinel out of reach. Rows enter the list on
              // INSERT (genuinely new) or when the user pages to them.
              if (!holderId && !parentLoaded) return;

              if (holderId && holderId === parentId && row.is_published !== true) {
                // Still where we hold it: merge, never replace. An unchanged TOAST column
                // (lyrics, rights) is absent from the payload, and a refetch per event would
                // cost one round trip per row of every family operation, in every open client
                // of this label.
                get().patchVersion(holderId, row.id, versionPatchFromPayload(row));
                return;
              }

              // It joined a family, left one, or went live — and the family it left is not on
              // the payload, because `old` carries the primary key only. Re-read whichever
              // parents are on screen; the debounce collapses a whole family's events into one.
              if (holderId) scheduleVersionsRefetch(holderId);
              if (parentLoaded && parentId !== holderId) scheduleVersionsRefetch(parentId);
              return;
            }

            // A row of the list that went live. Decided on the payload alone: `is_published` is
            // never a TOAST column, so it is always carried, and a colleague publishing a page
            // of tracks would otherwise cost one full-row read per track in every open client.
            if (row.is_published === true) {
              removeFromList(row.id, "published");
              return;
            }

            // The echo of this client's own family operation, for a row it has just written into
            // the list from the server's own response — and the payload agrees the row still
            // belongs (a draft, a main). Re-reading it would cost one round trip per row, and a
            // delete that frees a whole family produces up to `MAX_VERSIONS_PER_FAMILY` of them
            // at once. Only attach, detach, publish and delete claim ids this way; an ordinary
            // save does not, so a save still takes the full read below.
            if (
              get().ownPendingOps.has(row.id) &&
              row.is_published === false &&
              row.parent_track_id == null
            ) {
              return;
            }

            // Otherwise the payload may be partial (REPLICA IDENTITY DEFAULT), and mapping from
            // it would overwrite loaded values with undefined, so the decision is taken on the
            // full row — which is also the freshest one.
            const { data: freshRow } = await supabase
              .from("tracks")
              .select("*, profiles!tracks_user_id_profiles_fkey(display_name)")
              .eq("id", row.id)
              .single();

            if (!freshRow) return;

            if (freshRow.is_published) {
              // Published by a colleague, or by this client: the drafts query is
              // `is_published = false`, so the row is no longer one of these rows.
              removeFromList(row.id, "published");
              return;
            }
            if (freshRow.parent_track_id != null) {
              // Attached to another track: a version is no longer a main work.
              removeFromList(row.id, "attached");
              if (get().drafts.some((d) => d.id === freshRow.parent_track_id)) {
                scheduleVersionsRefetch(freshRow.parent_track_id);
              }
              return;
            }
            if (!matchesAppliedQuery(get().appliedQuery, freshRow)) {
              removeFromList(row.id, "filtered");
              return;
            }

            const mapped = await mapSupabaseTrackToDraft(freshRow);
            set((state) => ({
              drafts: state.drafts.map((d) =>
                d.id === mapped.id
                  ? // The family travels with the row. `mapped` comes from a single-row query
                    // that knows nothing about children, so replacing the row wholesale would
                    // drop the "+N versions" badge on every analysis completion and on the echo
                    // of every save the seller makes.
                    { ...mapped, version_count: d.version_count, versions: d.versions }
                  : d
              ),
            }));
          }
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            retryCount = 0; // Reset on successful connection
          } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
            // Clean up the failed channel
            if (channel) {
              // Only this channel: removeChannel() would close every channel in the app (lib/realtime.ts).
              leaveRealtimeChannel(channel);
              channel = null;
            }
            // Retry with backoff, up to MAX_RETRIES
            if (!cancelled && retryCount < MAX_RETRIES) {
              retryCount++;
              console.warn(`Drafts realtime subscription failed (attempt ${retryCount}/${MAX_RETRIES}), retrying in ${RETRY_DELAY_MS / 1000}s...`);
              setTimeout(() => {
                if (!cancelled) setupSubscription();
              }, RETRY_DELAY_MS);
            } else if (retryCount >= MAX_RETRIES) {
              console.error("Drafts realtime subscription failed after max retries. Live updates disabled.");
            }
          }
        });
    };

    setupSubscription();

    return () => {
      cancelled = true;
      if (channel) {
        leaveRealtimeChannel(channel);
        channel = null;
      }
    };
  },

  selectDraft: (id: string) => {
    set({ selectedDraftId: id, isDetailsOpen: true, initialTab: null });
  },

  openDraftToTab: (id: string, tab: string) => {
    set({ selectedDraftId: id, isDetailsOpen: true, initialTab: tab });
  },

  closeDetails: () => {
    set({ isDetailsOpen: false, selectedDraftId: null, initialTab: null });
  },

  toggleSelectionMode: () => {
    set((state) => ({
      isSelectionMode: !state.isSelectionMode,
      selectedDraftIds: new Set(), // Clear selection when toggling mode
    }));
  },

  toggleDraftSelection: (id: string) => {
    set((state) => {
      const newSelection = new Set(state.selectedDraftIds);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return { selectedDraftIds: newSelection };
    });
  },

  selectAllDrafts: () => {
    set((state) => {
      const allDraftIds = new Set(state.drafts.map((d) => d.id));
      return { selectedDraftIds: allDraftIds };
    });
  },

  clearSelection: () => {
    set({ selectedDraftIds: new Set() });
  },

  updateDraft: async (id: string, updates: Partial<Draft>) => {
    // Snapshot for rollback on failure
    const previousDraft = get().drafts.find((d) => d.id === id);

    // 1. Optimistically update local state (deep merge metadata to avoid wiping fields)
    set((state) => ({
      drafts: state.drafts.map((draft) =>
        draft.id === id
          ? {
              ...draft,
              ...updates,
              metadata: updates.metadata
                ? { ...draft.metadata, ...updates.metadata }
                : draft.metadata,
              lastModified: new Date().toISOString(),
            }
          : draft
      ),
    }));

    // 2. Persist changes to Supabase, respecting the actual schema
    const { metadata, rights, ...payloadForSupabase } = updates;

    // Include artist in the payload as it is a valid DB column

    // If metadata is being updated, flatten its properties into the payload
    // Only include fields that exist in the database schema
    if (metadata) {
      const validMetadataFields = [
        "bpm",
        "tempo",
        "key",
        "genre",
        "moods",
        "instruments",
        "vocal_gender",
        "vocal_type",
        "description",
        "release_date",
        "duration",
        "explicit_content",
        "language",
        "album",
        "isrc",
        "upc",
        "subgenre",
        "emotional_arc",
        "harmony",
        "chord_progression",
        "lyrical_theme",
        "cultural_fusion",
        "historical_period",
      ];

      // Fields that must be null (not empty string) in the database
      const nullableFields = ["release_date", "isrc", "upc", "album"];
      // Integer columns — coerce strings to numbers, empty to null
      const integerFields = ["duration", "bpm"];

      for (const [key, value] of Object.entries(metadata)) {
        if (validMetadataFields.includes(key)) {
          if (key === "genre" && typeof value === "string") {
            (payloadForSupabase as any)[key] = [value];
          } else if (integerFields.includes(key)) {
            if (value === "" || value === null || value === undefined) {
              (payloadForSupabase as any)[key] = null;
            } else {
              const num = Number(value);
              (payloadForSupabase as any)[key] = isNaN(num) ? null : num;
            }
          } else if (nullableFields.includes(key) && value === "") {
            (payloadForSupabase as any)[key] = null;
          } else {
            (payloadForSupabase as any)[key] = value;
          }
        }
      }
    }

    // If rights is being updated, include it in the payload
    if (rights) {
      (payloadForSupabase as any).rights = rights;
    }

    if (Object.keys(payloadForSupabase).length === 0) {
      return; // Nothing to update
    }

    payloadForSupabase.updated_at = new Date().toISOString();

    const { error } = await supabase
      .from("tracks")
      // payloadForSupabase is curated above from real columns (metadata whitelist +
      // spread of DB-backed Draft fields). Assert to the row-update shape at the boundary.
      // TODO: a typed Draft→tracks mapper would remove this cast (FRONTEND_TODO).
      .update(payloadForSupabase as TablesUpdate<"tracks">)
      .eq("id", id);

    if (error) {
      // Revert optimistic update
      if (previousDraft) {
        set((state) => ({
          drafts: state.drafts.map((d) =>
            d.id === id ? previousDraft : d
          ),
        }));
      }
      throw new Error(error.message || "Failed to update draft");
    }
  },

  /**
   * Publish one draft: its own row is saved here, the family flip is the server's.
   *
   * THE CLIENT NO LONGER FLIPS `is_published`. It writes the seller's edits and the licensing
   * columns, then asks `/api/track-families/publish`, which gates on the parent (analysed, has a
   * tier), copies the licence onto every version, embeds what can be embedded and flips the whole
   * family in ONE statement. The old client `/api/refresh-embedding` call is gone with it: the
   * endpoint embeds the parent first and writes nothing if that fails, so a live track without a
   * search entry can no longer be produced by giving up halfway.
   *
   * A refusal is re-thrown as a `TrackFamilyRefusal` so the caller can branch on `reason` —
   * `parent_no_tier` has a fix the seller can apply in place.
   */
  publishDraft: async (id: string) => {
    const draftToPublish = get().drafts.find((d) => d.id === id);
    if (!draftToPublish) {
      console.error("Draft not found:", id);
      throw new Error("Draft not found.");
    }

    // The server gates too. This only spares a round trip and names the field to fix.
    const licenseValidation = validateDraftLicense(draftToPublish);
    if (!licenseValidation.isValid) {
      const errorMsg =
        licenseValidation.errorMessage || "License validation failed";
      console.error(errorMsg);
      throw new Error(errorMsg);
    }

    await patchParentForPublish(draftToPublish);

    // Claim the echo first: realtime can deliver the flip before the response arrives, and the
    // seller who pressed Publish must not be told a colleague moved their track.
    get().markOwnPendingOps([id]);
    const [outcome] = await runFamilyPublish([id]);

    if (!outcome || outcome.error) {
      // The per-family error is exactly what a single-family call would have been refused with.
      const reason = (outcome?.error?.reason ?? "unknown") as TrackFamilyReason;
      throw new TrackFamilyRefusal(
        reason,
        outcome?.error?.message ?? refusalMessage(reason, "publish"),
        400
      );
    }

    // `withoutDraft` is a no-op when the realtime echo of this same publish already removed the
    // row, so the count drops exactly once.
    set((state) => ({ ...withoutDraft(state, id), error: null }));
    announcePublished([outcome]);
  },

  /**
   * Delete a draft, and decide what becomes of its versions (decision 8).
   *
   * The backend owns the cleanup — storage, the row, the junction tables via CASCADE — and, since
   * the family routes, the expansion too: `versions: "delete"` is the server reading the children
   * of this track and taking them with it. The client never sends an id list of its own, because
   * the drafts query loads the DRAFT half of a family only.
   *
   * `now_main_ids` is the other half of decision 8. Under `"detach"` the FK orphans the versions
   * into main tracks, which is what this endpoint has always done silently; they are named now, so
   * they can be put into the list here instead of appearing out of nowhere on the next refresh.
   */
  deleteDraft: async (id: string, options?: DeleteDraftOptions) => {
    const versions = options?.versions ?? "detach";
    try {
      interface DeleteResponse {
        success: boolean;
        deleted: string[];
        failed: { track_id: string; reason: string }[];
        unauthorized: string[];
        /** Rows refused for a reason of their own — a licence-request placeholder. */
        blocked?: { track_id: string; reason: string }[];
        /** Versions that went with the parent under `"delete"`. */
        expanded_version_ids?: string[];
        /** Versions the parent's deletion freed — main tracks of their own now. */
        now_main_ids?: string[];
        details: {
          storage_files_removed: number;
        };
      }

      // Claim the echo before the write: realtime can deliver the delete, and the re-parenting of
      // every version it frees, while the response is still in flight.
      get().markOwnPendingOps([id]);
      const response = await apiClient.post<DeleteResponse>(
        `/api/delete_tracks`,
        {
          track_ids: [id],
          versions,
          ...(options?.expectedVersions !== undefined
            ? { expected_versions: options.expectedVersions }
            : {}),
        }
      );

      if (response.deleted.includes(id)) {
        // The freed versions are put into the list from this same response, so their echoes need
        // neither a toast nor a row read of their own.
        const nowMainIds = response.now_main_ids ?? [];
        if (nowMainIds.length > 0) get().markOwnPendingOps(nowMainIds);
        set((state) => withDeletedDraft(state, id, nowMainIds));
        console.log(`Successfully deleted track ${id}`);
        return { success: true };
      }

      const refusal =
        response.failed.find((f) => f.track_id === id) ??
        response.blocked?.find((f) => f.track_id === id);
      throw new Error(
        refusal?.reason ||
          (response.unauthorized.includes(id) ? "Unauthorized" : "Delete failed")
      );
    } catch (error: unknown) {
      // A rule refusal (409 `family_changed`, 403, …) arrives as an ApiError carrying the server's
      // own sentence; anything else is ours to word. Either way the list is untouched.
      const refusal = error instanceof ApiError ? familyRefusalOf(error) : null;
      const errorMessage =
        refusal?.message ??
        (error instanceof Error ? error.message : "Failed to delete track");
      console.error("Failed to delete draft:", errorMessage);
      set({ error: errorMessage });
      return { success: false, error: errorMessage, reason: refusal?.reason };
    }
  },

  /**
   * Resolve metadata conflict between ACR recognition and embedded file tags.
   * Updates the track with the chosen source and clears the needs_metadata_review flag.
   */
  resolveMetadataConflict: async (id: string, source: "acr" | "embedded") => {
    try {
      interface ResolveResponse {
        success: boolean;
        track_id: string;
        source: string;
        updated_fields: string[];
      }

      const response = await apiClient.post<ResolveResponse>(
        `/api/tracks/${id}/resolve-metadata`,
        { source }
      );

      if (response.success) {
        // Update local draft state to reflect the resolution
        set((state) => ({
          drafts: state.drafts.map((draft) =>
            draft.id === id
              ? {
                  ...draft,
                  needs_metadata_review: false,
                  metadata_source: source,
                  // If embedded was chosen, update the visible fields
                  ...(source === "embedded" && draft.embedded_metadata
                    ? {
                        title: draft.embedded_metadata.title || draft.title,
                        artist: draft.embedded_metadata.artist || draft.artist,
                        metadata: {
                          ...(draft.metadata || {}),
                          album: draft.embedded_metadata.album || draft.metadata?.album,
                        },
                      }
                    : {}),
                }
              : draft
          ),
        }));
        console.log(`Successfully resolved metadata conflict for track ${id} using ${source}`);
        return { success: true };
      }

      throw new Error("Failed to resolve metadata conflict");
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to resolve metadata";
      console.error("Failed to resolve metadata conflict:", errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Publish several drafts at once — each one a family, each family independent.
   *
   * Order matters. Every parent's own row is saved FIRST (its licensing columns are what the
   * server copies onto the versions), and only then are the families handed over, in chunks of
   * `MAX_MAINS_PER_PUBLISH`: the endpoint embeds sequentially, so a longer list inside one
   * request would sit past the gateway's patience.
   *
   * A family that is refused stays in Drafts and is named. Every id reported here is a MAIN
   * track — `recentlyFailedIds` highlights rows of the list, and a version is not one of them.
   */
  publishMultipleDrafts: async (ids: string[]) => {
    const published: string[] = [];
    const failed: string[] = [];
    const errors: string[] = [];
    const failedDetails: Array<{ draft: Draft; error: string; reason?: string }> = [];

    const draftsToPublish = get().drafts.filter((d) => ids.includes(d.id));
    const byId = new Map(draftsToPublish.map((d) => [d.id, d]));
    const fail = (draft: Draft, error: string, reason?: string) => {
      if (failed.includes(draft.id)) return;
      failed.push(draft.id);
      errors.push(`${draft.title || draft.id}: ${error}`);
      failedDetails.push({ draft, error, reason });
    };

    // The same rule the server calls `parent_no_tier`, checked here so the dialog that follows
    // can offer the licence editor rather than only a sentence.
    const validation = validateMultipleDraftLicenses(draftsToPublish);
    validation.invalidDrafts.forEach(({ draft, error }) =>
      fail(draft, error, "parent_no_tier")
    );

    // Save each parent's edits before anything goes live. A row that cannot be saved is not
    // handed over: its family would go live carrying the licence it had before.
    const ready: Draft[] = [];
    for (const draft of validation.validDrafts) {
      try {
        await patchParentForPublish(draft);
        ready.push(draft);
      } catch (error: any) {
        fail(draft, error?.message || "Could not save this track before publishing.");
      }
    }

    const outcomes: FamilyPublishOutcome[] = [];
    for (let i = 0; i < ready.length; i += MAX_MAINS_PER_PUBLISH) {
      const chunk = ready.slice(i, i + MAX_MAINS_PER_PUBLISH);
      const chunkIds = chunk.map((d) => d.id);
      get().markOwnPendingOps(chunkIds);
      try {
        outcomes.push(...(await runFamilyPublish(chunkIds)));
      } catch (error: any) {
        // This chunk went unanswered and could not be read back either: none of it is claimed.
        const refusal = error instanceof TrackFamilyRefusal ? error : null;
        chunk.forEach((draft) =>
          fail(
            draft,
            refusal?.message || error?.message || "Publishing failed.",
            refusal?.reason
          )
        );
      }
    }

    for (const outcome of outcomes) {
      const draft = byId.get(outcome.mainId);
      if (outcome.error) {
        if (draft) fail(draft, outcome.error.message, outcome.error.reason);
        continue;
      }
      published.push(outcome.mainId);
      set((state) => withoutDraft(state, outcome.mainId));
    }

    if (failed.length > 0) {
      get().setRecentlyFailed(failed);
    }
    announcePublished(outcomes);

    return {
      success: published.length > 0,
      published,
      failed,
      errors,
      failedDetails,
    };
  },
}));
